'use client';

import { buttonVariants } from '@magicschool/spellbook/components/Button';
import type { IconProps } from '@magicschool/spellbook/components/Icon';
import { Icon } from '@magicschool/spellbook/components/Icon';
import {
  IconButtonIconSlot,
  IconButtonNotificationBadgeSlot,
  IconButtonSpinnerSlot,
  IconButtonToggleIconSlot,
} from '@magicschool/spellbook/components/IconButton/slots';
import { useAnimatedIcon } from '@magicschool/spellbook/hooks/useAnimatedIcon';
import { useRenderedChildren } from '@magicschool/spellbook/hooks/useRenderedChildren';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { getControlIconSize } from '@magicschool/spellbook/lib/controls';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui/slot';
import { memo } from 'react';

export type IconButtonProps = React.ComponentProps<'button'> &
  Pick<IconProps, 'animate' | 'name'> &
  Omit<VariantProps<typeof buttonVariants>, 'justify' | 'fullWidth' | 'isLoading'> & {
    /** Whether the icon button is rendering as a different component. Renders button styling on child element (e.g., `Link`) — enables navigation with button appearance. */
    asChild?: boolean;
    /** Accessible label for screen readers. Required for accessibility unless the button is wrapped in a `Tooltip` with `mode="label"`, which provides the accessible name via the tooltip content. The `label` prop renders as `sr-only` text (visually hidden, announced by screen readers). */
    label?: string;
    /** If true, the button will display a loading spinner icon and click functionality will be disabled. */
    isLoading?: boolean;
    'data-testid'?: string;
  };

type IconButtonContentProps = Pick<IconButtonProps, 'animate' | 'name' | 'size' | 'label' | 'children' | 'isLoading'> & {
  animating?: boolean;
  slots?: {
    loadingIcon?: React.ReactNode;
    icon?: React.ReactNode;
    toggleIcon?: React.ReactNode;
    notificationBadge?: React.ReactNode;
  };
  rest?: React.ReactNode[];
};

const IconButtonContent = memo<IconButtonContentProps>(
  ({ animate, animating, isLoading, name, label, size: providedSize, slots, rest }) => {
    if (isLoading) {
      return slots?.loadingIcon;
    }

    const size = getControlIconSize(providedSize);

    return (
      <>
        {slots?.icon ?? <Icon name={name} size={size} animate={animate} animating={animating} />}
        {(label || (rest && rest.length > 0)) && <span className="sr-only">{label || rest}</span>}
        {slots?.toggleIcon}
        {slots?.notificationBadge}
      </>
    );
  },
);

export const IconButtonBase: React.FC<IconButtonProps> = ({
  animate,
  asChild = false,
  className,
  name,
  size = 'md',
  shape = 'default',
  variant = 'solid',
  color = 'primary',
  isLoading = false,
  disabled,
  onClick,
  label,
  children,
  type = 'button',
  ...props
}) => {
  const Comp = asChild ? Slot : 'button';
  const isActuallyDisabled = disabled || isLoading;
  const resolvedAnimate = isActuallyDisabled ? undefined : animate;
  const { animating, getInteractProps } = useAnimatedIcon(resolvedAnimate);

  const { slots, rest } = useSlots(children, {
    loadingIcon: { Component: IconButtonSpinnerSlot, props: { size } },
    icon: { Component: IconButtonIconSlot, props: { name, size, animate: resolvedAnimate, animating } },
    toggleIcon: { Component: IconButtonToggleIconSlot, props: { size } },
    notificationBadge: {
      Component: IconButtonNotificationBadgeSlot,
      props: { buttonSize: size },
    },
  });

  const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isActuallyDisabled) {
      // explicitly prevent default and stop propagation so that `asChild` components are also disabled
      event.preventDefault();
      event.stopPropagation();

      return;
    }

    onClick?.(event);
  };

  const { renderedChildren } = useRenderedChildren(children, {
    renderContent: ({ rest }) => (
      <IconButtonContent
        animate={resolvedAnimate}
        animating={animating}
        name={name}
        size={size}
        label={label}
        isLoading={isLoading}
        slots={slots}
      >
        {rest}
      </IconButtonContent>
    ),
  });

  return (
    <Comp
      aria-disabled={isActuallyDisabled}
      aria-busy={isLoading}
      data-spellbook-name="icon-button"
      data-border={variant === 'outlined'}
      className={cn(
        buttonVariants({
          size,
          shape,
          variant,
          color,
          icon: true,
          hasToggleIcon: Boolean(slots.toggleIcon),
        }),
        slots.notificationBadge && 'relative overflow-visible',
        className,
      )}
      type={Comp === 'button' ? type : undefined}
      {...props}
      {...getInteractProps(props)}
      onClick={handleOnClick}
    >
      {asChild ? (
        renderedChildren
      ) : (
        <IconButtonContent
          animate={resolvedAnimate}
          animating={animating}
          name={name}
          size={size}
          label={label}
          isLoading={isLoading}
          slots={slots}
          rest={rest}
        />
      )}
    </Comp>
  );
};

/**
 * Icon-only button for compact actions where text labels don't fit. Shares styling with `Button` but enforces accessibility through required labeling.
 *
 * ### When to use
 * - For toolbar actions where space is limited (close, settings, menu)
 * - For compact UI elements (table row actions, card actions)
 * - When the icon's meaning is universally understood (X for close, gear for settings)
 * - As a trigger for tooltips, dropdowns, or popovers
 *
 * ### When NOT to use
 * - For primary actions: use `Button` with visible text for clarity
 * - For decorative icons: use `Icon` component instead
 * - For toggle states (on/off): use `ToggleIconButton` which provides `aria-pressed`
 * - When the action isn't immediately clear from the icon alone — use `Button` with text
 *
 * ### API
 * - `name`: Lucide icon name — optional, defaults to `square-dashed` when omitted (see https://lucide.dev for available icons)
 * - `label`: Screen reader text (required for accessibility) — provide via `label` prop OR wrap in `Tooltip` with `mode="label"`
 * - `variant`: Visual style: `solid` (default), `outlined`, `ghost`, `tooltip` (for tooltip triggers only)
 * - `color`: Semantic color from `Button` variants. Common values: `primary` (default), `neutral`, `error`, `disabled` (see `Button` docs for the full list).
 * - `size`: Button dimensions: `xs`, `sm`, `md` (default), `lg`
 * - `shape`: Border radius from `Button` variants. Common values: `default` (rounded square), `circle` (fully rounded), `square` (sharp corners).
 * - `isLoading`: Shows spinner, disables clicks, and sets `aria-busy="true"`
 * - `asChild`: Renders button styling on child element (e.g., `Link`)
 *
 * ### Composition (slots)
 * - `<IconButton.Spinner>`: Loading indicator. Rendered automatically when `isLoading={true}` — no need to include explicitly.
 * - `<IconButton.Icon>`: The button's icon. Renders the `name` icon at the control's icon size by default, so it is only needed to replace it: pass `overrideDefault` with custom children (a non-Lucide glyph, or a glyph with an overlay) sized to the control's icon.
 * - `<IconButton.ToggleIcon>`: Chevron that rotates on open/closed state. Only include when using dropdown/collapsible behavior — not rendered by default.
 * - `<IconButton.NotificationBadge>`: Badge overlay for unread counts or status indicators. Only include when displaying a notification — not rendered by default. Auto-positions based on button size.
 *
 * ### Visual states
 * - **Hover**: Background color shifts based on variant — `solid` lightens/darkens fill, `outlined`/`ghost` add subtle background
 * - **Active/Pressed**: Deeper background shift to indicate interaction
 * - **Loading**: Spinner replaces icon, pointer events blocked, `aria-busy="true"` set
 * - **Disabled**: Icon uses disabled color, background becomes transparent (except `solid`), cursor shows not-allowed, `aria-disabled="true"` set
 * - **Focus**: Focus ring appears around button for keyboard navigation
 * - **Open (dropdown/collapsible)**: Same as active state; `ToggleIcon` rotates 180° when `data-state="open"` or `aria-expanded="true"`
 *
 * ### Accessibility
 * - **REQUIRED**: Provide accessible label via `label` prop OR wrap in `Tooltip` with `mode="label"`. The `label` prop renders as `sr-only` text (visually hidden, announced by screen readers).
 * - Uses `aria-disabled` instead of `disabled` attribute to maintain focusability for screen readers. Pass `disabled` prop and the component handles `aria-disabled` and appropriate styles.
 * - Sets `aria-busy="true"` during loading state to announce to assistive technology. Pass `isLoading` prop and the component handles ARIA attributes and styles.
 * - Click handlers are blocked when disabled or loading, including for `asChild` components
 * - Keyboard: Enter/Space activates the button; focus is visible via ring outline
 *
 * ### `asChild` pattern
 * Use `asChild` to render a `Link` or other element with IconButton styling:
 * ```tsx
 * <IconButton asChild name="external-link" label="Open in new tab">
 *   <a href="https://example.com" target="_blank" rel="noopener noreferrer" />
 * </IconButton>
 * ```
 * Slot components (`ToggleIcon`, `NotificationBadge`) still work when using `asChild`.
 * ```tsx
 * <IconButton asChild name="more-horizontal" label="More options">
 *   <a href="#" className="group">
 *     <IconButton.ToggleIcon />
 *   </a>
 * </IconButton>
 * ```
 *
 * ### Tooltip integration
 * ```tsx
 * <Tooltip mode="label">
 *   <Tooltip.Trigger>
 *     <IconButton name="settings" />
 *   </Tooltip.Trigger>
 *   <Tooltip.Content>Settings</Tooltip.Content>
 * </Tooltip>
 * ```
 * When using `Tooltip` with `mode="label"`, the tooltip content provides the accessible name, so `label` prop is not needed.
 *
 * ### Additional notes
 * For Spellbook contributors: `IconButton` uses `buttonVariants` from `Button` internally, so variant/color/size styling is shared. Changes to `buttonVariants` affect all button components.
 */
export const IconButton = Object.assign(IconButtonBase, {
  Spinner: IconButtonSpinnerSlot,
  Icon: IconButtonIconSlot,
  ToggleIcon: IconButtonToggleIconSlot,
  NotificationBadge: IconButtonNotificationBadgeSlot,
});
